import { FaCamera, FaRegCheckCircle, FaRegIdCard, FaShieldAlt } from 'react-icons/fa';
import { Col } from 'reactstrap';
import { doGET } from "../../../util/HttpUtil";
import { useTranslation } from '../../../store/context/TranslationContext';
import { isEqual, isNil } from 'lodash';
import {
    FORM_TYPE,
    FORM_TYPE_LABELS,
    getApplicationTitle,
    getBusinessFamilyLabel,
    getFormTypeLabel,
} from "../../../util/applicationLabels";
import { buildAutomationChildTestId, resolveAutomationTestId } from '../../../components/InputField/testId';

export {
    FORM_TYPE,
    FORM_TYPE_LABELS,
    getApplicationTitle,
    getBusinessFamilyLabel,
    getFormTypeLabel,
};

export const STEPS = {
    APPLICATION_FORM: "Application Form",
    DOCUMENT_VERIFICATION: "Document Verification",
    KYC_VERIFICATION: "KYC Verification",
    SANCTIONS_SCREENING: "Sanctions Screening",
    RISK_ASSESSMENT: "Risk Assessment"
}

export const APPLICATION_STATUS = {
    DRAFT: 0,
    SUBMITTED: 1,
    INCOMPLETE: 2,
    APPROVED: 3,
    REJECTED: 4,
    ACCOUNT_REQUESTED: 5,
    ACCOUNT_CREATED: 6,
    ACCOUNT_CREATION_FAILED: 7,
    DECLINED: 8,
    ARCHIVED: 9
}

export const STEP_STATUS = {
    DRAFT: 0,
    SUBMITTED: 1,
    INCOMPLETE: 2,
    APPROVED: 3,
    REJECTED: 4,
}

export const checkIsAskForInfo = (steps) => {
    return (steps ?? [])?.find((v) => v?.status == 2)
}

export const STEP_NUM = {
    USER_DETAILS: 1,
    DECLARATION_FORM: 2,
    DOCUMENT_VERIFICATION: 3,
    PAY_NOW: 4,
    KYC_VERIFICATION: 5,
    APPLICATION_FORM_REDO: 6,
    THANK_YOU: Number.MAX_SAFE_INTEGER
}

export const IPLUS_WIZARD = {
    IPlusUserDetailForm: 'IPlusUserDetailForm',
    IPlusDeclarationForm: 'IPlusDeclarationForm',
    IPlusAboutYou: 'IPlusAboutYou',
    IPlusDigialAssestsCustody: 'IPlusDigialAssestsCustody',
    EnhancedDueDiligenceContent: 'EnhancedDueDiligenceContent',
    ExtraQuestions: 'ExtraQuestions',
    ApplicationDocRedo: 'ApplicationDocRedo',
    ThankYou: 'ThankYou',
    Documents: 'ProofOfAddress',
    KYCVerification: 'KYCVerification',
    IPlusAccountActivityDeclaration: 'IPlusAccountActivityDeclaration',
    IPlusOccupationDetails: 'IPlusOccupationDetails',
}

export const IPLUS_WIZARD_NUM = {
    IPlusUserDetailForm: 1,
    IPlusDeclarationForm: 2,
    IPlusAboutYou: 2,
    IPlusAccountActivityDeclaration: 2,
    IPlusDigialAssestsCustody: 2,
    EnhancedDueDiligenceContent: 0,
    ExtraQuestions: 6,
    ApplicationDocRedo: 6,
    ThankYou: Number.MAX_SAFE_INTEGER,
    Documents: 3,
    KYCVerification: 5,
    IPlusOccupationDetails: 2,
}

export const getIPlusStepNumberWhenRedo = (step) => {
    switch (step) {
        case "Application Form":
            return STEP_NUM.APPLICATION_FORM_REDO;
        case "KYC Verification":
            return STEP_NUM.THANK_YOU;
        case "Sanctions Screening":
            return STEP_NUM.THANK_YOU;
        case "Document Verification":
            return STEP_NUM.THANK_YOU;
        case "Risk Assessment":
            return STEP_NUM.THANK_YOU;
        default:
            return 0;
    }
}

export const getIPlusWizardWhenRedo = (step) => {
    switch (step) {
        case "Application Form":
            return { wizard: IPLUS_WIZARD.ApplicationDocRedo };
        case "KYC Verification":
            return { wizard: IPLUS_WIZARD.ThankYou };
        case "Sanctions Screening":
            return { wizard: IPLUS_WIZARD.ThankYou };
        case "Document Verification":
            return { wizard: IPLUS_WIZARD.ThankYou };
        case "Risk Assessment":
            return { wizard: IPLUS_WIZARD.ThankYou };
        default:
            return { wizard: IPLUS_WIZARD.ThankYou };;
    }
}

export const convertArrayToDict = (array) => {
    return array.reduce((acc, obj) => {
        if (obj.label && !acc[obj.label]) {
            acc[obj.label] = obj;
        }
        return acc;
    }, {});
}

export const fetchIndividualLiteData = async (formId) => {
    try {
        const response = await doGET(`/api/individual-lite/detail?id=${formId}`);
        return response.data ?? {};
    } catch (error) {
        console.log(error);
        return {};
    }
};

export const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth' // Smooth scrolling effect
    });
};

export const StaticKYCContent = ({ testId } = {}) => {
    const { t } = useTranslation();
    const resolvedTestId = resolveAutomationTestId(testId);

    return <Col className="user-note-content mt-4 col-sm-6" data-testid={resolvedTestId}>
        <div className="user-note-item d-flex mt-2" data-testid={buildAutomationChildTestId(resolvedTestId, "id-document-note")}>
            <FaRegIdCard className="icon" size={20} />
            <span className='px-2 mt-1' style={{ fontSize: "16px" }}>{t("You will need to provide a valid ID document for verification.")}</span>
        </div>
        <div className="user-note-item d-flex mt-2" data-testid={buildAutomationChildTestId(resolvedTestId, "selfie-note")}>
            <FaCamera className="icon" size={20} />
            <span className='px-2 mt-1' style={{ fontSize: "16px" }}>{t("Be ready to take a live selfie to confirm your identity.")}</span>
        </div>
        <div className="user-note-item d-flex mt-2" data-testid={buildAutomationChildTestId(resolvedTestId, "photo-quality-note")}>
            <FaRegCheckCircle className="icon" size={20} />
            <span className='px-2 mt-2' style={{ fontSize: "16px" }}>{t("Make sure to follow the instructions for a clear and accurate photo capture.")}</span>
        </div>
        <div className="user-note-item d-flex mt-2" data-testid={buildAutomationChildTestId(resolvedTestId, "security-note")}>
            <FaShieldAlt className="icon" size={20} />
            <span className='px-2' style={{ fontSize: "16px" }}>{t("Your data is protected with us. We adhere to the highest security and compliance standards.")}</span>
        </div>
    </Col>
}

export const TRANSACTION_FIELDS = [
    "Average_Balance",
    "Number_Of_Monthly_Ach_Deposits",
    "Amount_Of_Monthly_Ach_Deposits",
    "Number_Of_Monthly_Domestic_Wire_Deposits",
    "Amount_Of_Monthly_Domestic_Wire_Deposits",
    "Number_Of_Monthly_International_Wire_Deposits",
    "Amount_Of_Monthly_International_Wire_Deposits",
    "Number_Of_Monthly_Ach_Payments",
    "Amount_Of_Monthly_Ach_Payments",
    "Number_Of_Monthly_Domestic_Wire_Payments",
    "Amount_Of_Monthly_Domestic_Wire_Payments",
    "Number_Of_Monthly_International_Wire_Payments",
    "Amount_Of_Monthly_International_Wire_Payments",
    "Number_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Amount_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Number_Of_Monthly_Stablecoin_Conversions_Payments",
    "Amount_Of_Monthly_Stablecoin_Conversions_Payments",
    "Count_Of_Monthly_Cryptocurrency_Conversions",
    "Amount_Of_Monthly_Cryptocurrency_Conversions",
]

export const areTransactionValuesSame = (object1, object2) => {
    return TRANSACTION_FIELDS.every(field => !isNil(object1[field]) && !isNil(object2[field]) && isEqual(object1[field], object2[field]));
};

export const isObjectNewTransactionsEmpty = (object) => {
    return TRANSACTION_FIELDS.every(field => isNil(object[field]));
};

export const getSelfdeclaredValues = (data) => {
    const diffMap = data?.diffMap || {};
    return TRANSACTION_FIELDS.reduce((acc, field) => {
        const value = !isNil(diffMap[field]) ? diffMap[field] : data?.[field];
        if (!isNil(value)) acc[field] = value;
        return acc;
    }, {});
};

export const formatActualTransactions = (apiDataResponse) => {
    return {
        Average_Balance: apiDataResponse["Actual Average Balance(USD)"],
        Number_Of_Monthly_Ach_Deposits: apiDataResponse["ACH Deposits"]["Actual number of monthly ACH deposits"],
        Amount_Of_Monthly_Ach_Deposits: apiDataResponse["ACH Deposits"]["Actual amount of monthly ACH deposits"],
        Number_Of_Monthly_Domestic_Wire_Deposits: apiDataResponse["Domestic Wire Deposits"]["Actual number of monthly domestic wire deposits"],
        Amount_Of_Monthly_Domestic_Wire_Deposits: apiDataResponse["Domestic Wire Deposits"]["Actual amount of monthly domestic wire deposits"],
        Number_Of_Monthly_International_Wire_Deposits: apiDataResponse["International wire Deposits"]["Actual number of monthly international wire deposits"],
        Amount_Of_Monthly_International_Wire_Deposits: apiDataResponse["International wire Deposits"]["Actual amount of monthly international wire deposits"],
        Number_Of_Monthly_Ach_Payments: apiDataResponse["ACH Payments"]["Actual number of monthly ACH payments"],
        Amount_Of_Monthly_Ach_Payments: apiDataResponse["ACH Payments"]["Actual amount of monthly ACH payments"],
        Number_Of_Monthly_Domestic_Wire_Payments: apiDataResponse["Domestic Wire Payments"]["Actual number of monthly domestic wire payments"],
        Amount_Of_Monthly_Domestic_Wire_Payments: apiDataResponse["Domestic Wire Payments"]["Actual amount of monthly domestic wire payments"],
        Number_Of_Monthly_International_Wire_Payments: apiDataResponse["International wire Payments"]["Actual number of monthly International wire payments"],
        Amount_Of_Monthly_International_Wire_Payments: apiDataResponse["International wire Payments"]["Actual amount of monthly International wire payments"],
        Number_Of_Monthly_Stablecoin_Conversions_Deposits: apiDataResponse["Stablecoin Deposits"]["Actual count of monthly stablecoin conversions (Deposits)"],
        Amount_Of_Monthly_Stablecoin_Conversions_Deposits: apiDataResponse["Stablecoin Deposits"]["Actual amount of monthly stablecoin conversions (Deposits)"],
        Number_Of_Monthly_Stablecoin_Conversions_Payments: apiDataResponse["Stablecoin Payments"]["Actual count of monthly stablecoin conversions (Payments)"],
        Amount_Of_Monthly_Stablecoin_Conversions_Payments: apiDataResponse["Stablecoin Payments"]["Actual amount of monthly stablecoin conversions (Payments)"],
        Count_Of_Monthly_Cryptocurrency_Conversions: apiDataResponse["Actual count of monthly cryptocurrency conversions"],
        Amount_Of_Monthly_Cryptocurrency_Conversions: apiDataResponse["Actual amount of monthly cryptocurrency conversions"]
    };
}
